import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Autocomplete,
  TextField,
  CircularProgress,
  Box,
  Typography,
  FormHelperText
} from '@mui/material';
import clientService from '../../features/clients/services/clientService';
import { logger } from '../../utils/logger';

// Global cache shared across all ClientSelect instances
const globalCache = {
  clients: [],
  timestamp: 0,
  loading: false,
  promise: null,
  searchCache: new Map(), // Cache search results (scoped by clientListMode)
  listMode: 'all',
  CACHE_DURATION: 60000, // 1 minute cache
};

// Global function to load clients (shared across instances)
const loadClientsGlobal = async (searchTerm = '', clientListMode = 'all') => {
  const cacheKey = searchTerm.trim().toLowerCase();
  const now = Date.now();

  if (globalCache.listMode !== clientListMode) {
    globalCache.clients = [];
    globalCache.timestamp = 0;
    globalCache.searchCache.clear();
    globalCache.listMode = clientListMode;
  }

  const scopedSearchKey = `${clientListMode}::${cacheKey}`;

  // Check search cache first
  if (cacheKey && globalCache.searchCache.has(scopedSearchKey)) {
    const cached = globalCache.searchCache.get(scopedSearchKey);
    if (now - cached.timestamp < globalCache.CACHE_DURATION) {
      return cached.data;
    }
  }

  // For empty search, check main cache
  if (!cacheKey && globalCache.clients.length > 0 && now - globalCache.timestamp < globalCache.CACHE_DURATION) {
    return globalCache.clients;
  }

  // If already loading the same request, wait for it
  if (globalCache.loading && globalCache.promise) {
    return globalCache.promise;
  }

  // Start new request
  globalCache.loading = true;

  const params = { limit: 50 };
  if (cacheKey) {
    params.search = cacheKey;
  }
  if (clientListMode === 'active') {
    params.is_active = true;
  } else if (clientListMode === 'inactive') {
    params.is_active = false;
  }

  globalCache.promise = clientService.getAll(params)
    .then((response) => {
      if (response.success) {
        const data = response.data || [];

        if (!cacheKey) {
          // Cache main list
          globalCache.clients = data;
          globalCache.timestamp = now;
        } else {
          // Cache search results
          globalCache.searchCache.set(scopedSearchKey, { data, timestamp: now });
        }

        return data;
      }
      return [];
    })
    .catch((err) => {
      logger.error('ClientSelect: error loading clients', err);
      return [];
    })
    .finally(() => {
      globalCache.loading = false;
      globalCache.promise = null;
    });
  
  return globalCache.promise;
};

/** Ένα request ανά id — αποφεύγει διπλό GET /clients/:id από πολλά ClientSelect ή re-run effect. */
const pendingClientById = new Map();

function fetchClientByIdOnce(clientId) {
  if (pendingClientById.has(clientId)) {
    return pendingClientById.get(clientId);
  }
  const p = clientService
    .getById(clientId)
    .finally(() => {
      pendingClientById.delete(clientId);
    });
  pendingClientById.set(clientId, p);
  return p;
}

const ClientSelect = ({
  value,
  onChange,
  error = false,
  helperText,
  required = false,
  disabled = false,
  label = 'Πελάτης',
  placeholder = 'Επιλέξτε ή αναζητήστε πελάτη...',
  size = 'medium',
  name,
  /** 'all' | 'active' | 'inactive' — φίλτρο λίστας πελατών (API is_active) */
  clientListMode = 'all',
  ...props
}) => {
  const [options, setOptions] = useState(globalCache.clients);
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [selectedClient, setSelectedClient] = useState(null);
  
  const debounceTimerRef = useRef(null);
  const mountedRef = useRef(true);

  // Load clients with global cache
  const loadClients = useCallback(async (searchTerm = '') => {
    setLoading(true);
    try {
      const data = await loadClientsGlobal(searchTerm, clientListMode);
      if (mountedRef.current) {
        setOptions(data);
      }
    } finally {
      if (mountedRef.current) {
        setLoading(false);
      }
    }
  }, [clientListMode]);

  // Φόρτωση λίστας (αρχική + όταν αλλάζει clientListMode)
  useEffect(() => {
    mountedRef.current = true;

    if (globalCache.clients.length > 0 && globalCache.listMode === clientListMode) {
      setOptions(globalCache.clients);
    }

    loadClients('');

    return () => {
      mountedRef.current = false;
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [loadClients, clientListMode]);

  // Sync selected client with value prop
  useEffect(() => {
    if (value === undefined || value === null || value === '') {
      setSelectedClient(null);
      return;
    }
    const numericId = Number(value);
    if (!Number.isFinite(numericId)) {
      setSelectedClient(null);
      return;
    }
    
    // Check if already selected
    if (selectedClient && Number(selectedClient.id) === numericId) {
      return;
    }
    
    // Check in current options
    const match = options.find((client) => Number(client.id) === numericId);
    if (match) {
      setSelectedClient(match);
    } else {
      // Fetch single client by ID
      fetchClientByIdOnce(numericId)
        .then((response) => {
          if (response.success && response.data && mountedRef.current) {
            setOptions((prev) => {
              const exists = prev.some((client) => Number(client.id) === numericId);
              if (exists) return prev;
              return [response.data, ...prev];
            });
            setSelectedClient(response.data);
          }
        })
        .catch((err) => {
          logger.error('ClientSelect: error fetching client by id', err);
        });
    }
  }, [value, options, selectedClient]);

  // Handle search input change with debounce
  const handleInputChange = useCallback((event, newInputValue, reason) => {
    setInputValue(newInputValue);
    
    // Only search when user types
    if (reason !== 'input') return;
    
    // Clear previous timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    
    // Debounce search
    debounceTimerRef.current = setTimeout(() => {
      loadClients(newInputValue);
    }, 300);
  }, [loadClients]);

  // Handle selection change
  const handleValueChange = useCallback((newValue) => {
    setSelectedClient(newValue);
    if (onChange) {
      const selectedId =
        newValue && newValue.id !== undefined && newValue.id !== null
          ? Number(newValue.id)
          : '';
      logger.debug('ClientSelect: handleValueChange', { selectedId });
      if (name) {
        onChange(name, selectedId);
      } else {
        onChange(selectedId);
      }
    }
  }, [onChange, name]);

  return (
    <Box>
      <Autocomplete
        options={options}
        value={selectedClient}
        onChange={(event, newValue) => handleValueChange(newValue)}
        onInputChange={handleInputChange}
        loading={loading}
        disabled={disabled}
        getOptionLabel={(option) => {
          if (typeof option === 'string') return option;
          return option?.name || '';
        }}
        isOptionEqualToValue={(option, value) => {
          if (!option || !value) return false;
          return option.id === value.id;
        }}
        renderOption={(props, option) => {
          // Get first project domain for display
          const firstProjectDomain = option.projects && option.projects.length > 0 && option.projects[0].domain
            ? option.projects[0].domain
            : null;
          
          return (
            <Box component="li" {...props} key={option.id}>
              <Box>
                <Typography variant="body1">
                  {option.name}
                </Typography>
                {firstProjectDomain && (
                  <Typography variant="caption" color="text.secondary">
                    {firstProjectDomain}
                  </Typography>
                )}
              </Box>
            </Box>
          );
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            placeholder={placeholder}
            required={required}
            error={error}
            size={size}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? <CircularProgress color="inherit" size={20} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        noOptionsText={
          inputValue ? 'Δεν βρέθηκαν πελάτες' : 'Πληκτρολογήστε για αναζήτηση...'
        }
        loadingText="Φόρτωση..."
        {...props}
      />
      {helperText && (
        <FormHelperText error={error} sx={{ mt: 0.5, mx: 1.75 }}>
          {helperText}
        </FormHelperText>
      )}
    </Box>
  );
};

export default ClientSelect;

